import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import InputField from "../../../../components/InputField";
import api from "../../../../api/api";
import Button from "../../../../components/Button";
import { Modal } from "react-bootstrap";
import Breadcrumbs from "../../../../components/Breadcrumbs";

const ViewBookingAdmin = () => {
  const { id } = useParams();
  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    const fetchEnquiry = async () => {
      try {
        const response = await api.get(`/enquiry/viewEnquiry/${id}`);
        setEnquiry(response.data);
      } catch (err) {
        console.error("Error fetching enquiry:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchEnquiry();
  }, [id]);

  const handleSendEmail = () => {
    setShowEmailModal(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailRecipients.trim()) {
      setEmailError("Please enter at least one email address.");
      return;
    }
    setEmailLoading(true);
    setEmailError("");
    try {
      await api.post("/booking/send-booking-confirmation-email", {
        recipients: emailRecipients,
        enquiryId: id,
      });
      setEmailSuccess(true);
      setShowEmailModal(false);
      setEmailRecipients("");
    } catch (err) {
      console.error("Error sending email:", err);
      setEmailError("Failed to send email. Please try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailModalClose = () => {
    setShowEmailModal(false);
    setEmailRecipients("");
    setEmailError("");
  };

  const handleSuccessClose = () => {
    setEmailSuccess(false);
  };

  if (loading) return <p>Loading enquiry...</p>;
  if (error) return <p>Failed to load enquiry.</p>;
  if (!enquiry) return <p>No enquiry found.</p>;

  return (
    <>
      <Breadcrumbs />
      <form>
        <div className="row g-3">
          <div className="col-sm-6 col-md-4">
            <InputField label="Incoterm" value={enquiry.incoterm} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Shipper Name" value={enquiry.first_shipper_name || ""} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Consignee Name" value={enquiry.first_consignee_name || ""} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Shipment Segment" value={enquiry.shipment_segment} disabled />
          </div>
          {["Door to Door", "ICD to Door", "Door to Port", "ICD to Port"].includes(enquiry.incoterm) && (
            <>
              <div className="col-sm-6 col-md-4">
                <InputField label="Place Of Receipt" value={enquiry.place_of_receipt} disabled />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField label="POR Pin Code" value={enquiry.place_of_receipt_pin} disabled />
              </div>
            </>
          )}
          <div className="col-sm-6 col-md-4">
            <InputField label="Port of Loading" value={enquiry.pol} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Port of Discharge" value={enquiry.pod} disabled />
          </div>
          {["Door to Door", "ICD to Door", "Port to Door"].includes(enquiry.incoterm) && (
            <>
              <div className="col-sm-6 col-md-4">
                <InputField label="Final Port Of Delivery" value={enquiry.final_port_of_delivery} disabled />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField label="FPOD Pin Code" value={enquiry.final_port_of_delivery_pin} disabled />
              </div>
            </>
          )}
          <div className="col-sm-6 col-md-4">
            <InputField label="Freight Terms" value={enquiry.freight_terms} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Routing" value={enquiry.routing || ""} disabled />
          </div>
          {["Indirect", "Both"].includes(enquiry.routing) && (
            <div className="col-sm-6 col-md-4">
              <InputField label="Transshipment Hub" value={enquiry.transshipment_hub || ""} disabled />
            </div>
          )}
          {["FE", "LE"].includes(enquiry.shipment_segment) && (
            <div className="col-sm-6 col-md-4">
              <InputField label="Stuffing At" value={enquiry.stuffing_destuffing_At} disabled />
            </div>
          )}
          {["LI", "FI"].includes(enquiry.shipment_segment) && (
            <div className="col-sm-6 col-md-4">
              <InputField label="Destuffing At" value={enquiry.stuffing_destuffing_at} disabled />
            </div>
          )}
          <div className="col-sm-6 col-md-4">
            <InputField label="Nature of Cargo" value={enquiry.nature_of_cargo} disabled />
          </div>
          {enquiry.nature_of_cargo === "Hazardous" && (
            <div className="col-sm-6 col-md-4">
              <InputField label="HAZ Class" value={enquiry.haz_class} disabled />
            </div>
          )}
          <div className="col-sm-6 col-md-4">
            <InputField label="HS Code" value={enquiry.hs_code} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="HSN Description" value={enquiry.hs_description} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Document Type" value={enquiry.document_type} disabled />
          </div>
          {enquiry.document_type === "Original" && (
            <div className="col-sm-6 col-md-4">
              <InputField label="Document Count" value={enquiry.document_count} disabled />
            </div>
          )}
          {enquiry.switch_bl === "Yes" && (
            <div className="col-sm-6 col-md-4">
              <InputField label="Switch BL" value={enquiry.switch_at} disabled />
            </div>
          )}
          <div className="col-sm-6 col-md-4">
            <InputField label="Packaging Type" value={enquiry.packaging_type} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Packaging Count" value={enquiry.packaging_count} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Goods Value" value={enquiry.goods_value} disabled />
          </div>

          {/* Container Details */}
          {enquiry.containerDetails?.length > 0 && (
            <div className="col-12 mt-3">
              <h5 className="mb-2">Container Details</h5>
              <div className="position-relative mb-4">
                <div className="d-flex align-items-start">
                  <div className="ovrflx flex-grow-1">
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            {enquiry.shipment_segment !== "LE" &&
                              enquiry.shipment_segment !== "LI" && (
                                <th>Container Type</th>
                              )}
                            {!["LE", "LI"].includes(enquiry.shipment_segment) && (
                              <th>Gross Cargo Weight</th>
                            )}
                            {!["FE", "FI"].includes(enquiry.shipment_segment) && (
                              <th>Length</th>
                            )}
                            {!["FE", "FI"].includes(enquiry.shipment_segment) && (
                              <th>Breadth</th>
                            )}
                            {!["FE", "FI"].includes(enquiry.shipment_segment) && (
                              <th>Height</th>
                            )}
                            {["LE", "LI"].includes(enquiry.shipment_segment) && (
                              <th>CBM</th>
                            )}
                            {enquiry.nature_of_cargo === "Temperature Control" && (
                              <th>Temperature</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {enquiry.containerDetails.map((container, idx) => (
                            <tr key={idx}>
                              {enquiry.shipment_segment !== "LE" &&
                                enquiry.shipment_segment !== "LI" && (
                                  <td>{container.container_type}</td>
                                )}
                              {!["LE", "LI"].includes(enquiry.shipment_segment) && (
                                <td>{container.container_weight}</td>
                              )}
                              {!["FE", "FI"].includes(enquiry.shipment_segment) && (
                                <td>{container.container_length}</td>
                              )}
                              {!["FE", "FI"].includes(enquiry.shipment_segment) && (
                                <td>{container.container_breadth}</td>
                              )}
                              {!["FE", "FI"].includes(enquiry.shipment_segment) && (
                                <td>{container.container_height}</td>
                              )}
                              {["LE", "LI"].includes(enquiry.shipment_segment) && (
                                <td>
                                  {container.container_length && container.container_breadth && container.container_height
                                    ? (
                                        (parseFloat(container.container_length) *
                                          parseFloat(container.container_breadth) *
                                          parseFloat(container.container_height)) /
                                        1000000
                                      ).toFixed(2)
                                    : "0.00"}
                                </td>
                              )}
                              {enquiry.nature_of_cargo === "Temperature Control" && (
                                <td>{container.container_temperature}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="measurement d-none d-lg-block ms-3 flex-shrink-0">
                    <p>1M = 100CM</p>
                    <p>1CM = 10MM</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="col-sm-6 col-md-4">
            <InputField label="CHA Services Required" value={enquiry.cha_required || ""} disabled />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField label="Remarks" value={enquiry.remarks} disabled />
          </div>
        </div>
        <div className="d-flex justify-content-end mt-4">
          <Button
            text="Send Email"
            variant="primary"
            onClick={handleSendEmail}
          />
        </div>
      </form>

      {/* Email Modal */}
      <Modal show={showEmailModal} onHide={handleEmailModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send Booking Confirmation Email</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleEmailSubmit}>
          <Modal.Body>
            <div className="mb-3">
              <label htmlFor="emailRecipients" className="form-label">
                Email Addresses (comma-separated)
              </label>
              <input
                type="text"
                className="form-control"
                id="emailRecipients"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                placeholder="e.g., user1@example.com, user2@example.com"
                required
              />
              {emailError && (
                <div className="text-danger mt-1">{emailError}</div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              text="Cancel"
              variant="secondary"
              onClick={handleEmailModalClose}
            />
            <Button
              text={emailLoading ? "Sending..." : "Send Email"}
              variant="primary"
              type="submit"
              disabled={emailLoading}
            />
          </Modal.Footer>
        </form>
      </Modal>

      {/* Success Modal */}
      <Modal show={emailSuccess} onHide={handleSuccessClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Booking confirmation email sent successfully!</Modal.Body>
        <Modal.Footer>
          <Button text="OK" variant="success" onClick={handleSuccessClose} />
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default ViewBookingAdmin;
